import React, { useState, useEffect } from 'react';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Input } from '@progress/kendo-react-inputs';
import { Switch } from '@progress/kendo-react-inputs';
import { useTranslation } from 'react-i18next';

function AddNewObjectDialog({ visible, onClose, onSubmit, values, onChange, entities = [], maxEntities, isEntityNameUsed }) {
    const { t } = useTranslation("create_project/alice_recommended_entities", { useSuspense: false });
    const [errors, setErrors] = useState({ isNameValid: true, nameError: "" });
    const [touched, setTouched] = useState(false);
    const [maxEntitiesError, setMaxEntitiesError] = useState("");

    // Remove special characters utility (from CreateEntityDialog)
    const removeSpecialCharacters = (str) => str.replace(/[^a-zA-Z0-9\s]/g, "");

    // Validation logic adapted from CreateEntityDialog
    const validateName = (name) => {
        let error = { isNameValid: true, nameError: "" };
        const trimmed = name.trim();
        // Use isEntityNameUsed prop if provided
        const entityNameInUse = typeof isEntityNameUsed === 'function' ? isEntityNameUsed(trimmed) : (entities.findIndex(entity => removeSpecialCharacters(entity.name.trim().toLowerCase()) === removeSpecialCharacters(trimmed.toLowerCase())) > -1);
        if (trimmed === "") {
            error.isNameValid = false;
            error.nameError = t("entity.create_dialog.errors.name_required", "Name is required");
        } else if (/^\d/.test(trimmed)) {
            error.isNameValid = false;
            error.nameError = t("entity.create_dialog.errors.name_can_not_start_with_number", "Name cannot start with a number");
        } else if (entityNameInUse) {
            error.isNameValid = false;
            error.nameError = t("entity.create_dialog.errors.name_exists", "Object already exists");
        } else if (trimmed.length > 30) {
            error.isNameValid = false;
            error.nameError = t("entity.create_dialog.errors.name_length_exceeded", "Name length exceeded");
        }
        return error;
    };

    useEffect(() => {
        if (touched) {
            setErrors(validateName(values.name || ""));
        } else {
            setErrors({ isNameValid: true, nameError: "" });
        }
        // eslint-disable-next-line
    }, [values.name, entities, touched]);

    const handleNameChange = (e) => {
        if (!touched) setTouched(true);
        const newValues = { ...values, name: e.target.value };
        onChange(newValues);
        setErrors(validateName(e.target.value));
    };

    useEffect(() => {
        if (visible) setTouched(false);
    }, [visible]);

    const handleAdd = () => {
        if (typeof maxEntities === 'number') {
            const checkedEntities = entities.filter(entity => entity.checked).length;
            if (checkedEntities >= maxEntities) {
                setMaxEntitiesError(t('drawer.add_new_object.max_entities_exceeded', 'Maximum number of objects reached'));
                return;
            }
        }
        setMaxEntitiesError("");
        onSubmit(values);
    };

    const isAddDisabled = !errors.isNameValid || (values.name || "").trim() === "";

    return (
        <Dialog title={null} onClose={onClose} minWidth={500} className="add-object-dialog-custom">
            {/* Close X button */}
            <div className="p-1 px-2">
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <h5 className="fw-bold mb-0">{t('drawer.add_new_object.title')}</h5>
                    <button
                        type="button"
                        className="btn-close"
                        aria-label="Close"
                        style={{ zIndex: 2 }}
                        onClick={onClose}
                    />
                </div>
                <div className="mb-4">
                    <label className="form-label" required>{t('drawer.add_new_object.object_name')}</label>
                    <input
                        value={values.name}
                        className={`form-control${!errors.isNameValid && touched ? ' is-invalid' : ''}`}
                        onChange={handleNameChange}
                        placeholder={t('drawer.add_new_object.object_name_placeholder')}
                    />
                    {!errors.isNameValid && touched && (
                        <div className="invalid-feedback d-block">{errors.nameError}</div>
                    )}
                </div>
                <div className="mb-4 d-flex align-items-center justify-content-between">
                    <span>{t('drawer.add_new_object.let_ai_autofill')}</span>
                    <Switch
                        checked={values.autofill}
                        onChange={e => onChange({ ...values, autofill: e.target.value })}
                    />
                </div>
                <div className="text-danger mb-2" role="alert">
                    { maxEntitiesError }
                </div>
                <div className="d-flex justify-content-end gap-2">
                    <button className="btn btn-secondary" onClick={onClose}>{t('drawer.add_new_object.cancel')}</button>
                    <button className="btn btn-primary px-4" onClick={handleAdd} disabled={isAddDisabled}>{t('drawer.add_new_object.add')}</button>
                </div>
            </div>
        </Dialog>
    );
}

export default AddNewObjectDialog;
